import React, { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * 6-box auto-advancing OTP input. Mirrors the Elevante login/reset
 * flow — pure controlled component, no internal state.
 *
 * Props:
 *   value      — string, current code ("" | "1" | "12" | … | "123456")
 *   onChange   — (nextValue: string) => void  (called on every keystroke)
 *   onComplete — (fullCode: string) => void   (called once when 6 digits typed)
 *   length     — default 6
 *   autoFocus  — default true
 *   disabled   — bool
 *   error      — bool (renders in rose-500 border)
 *   testIdPrefix — default 'otp-input'  → `${prefix}-${i}` per box
 */
export default function OtpInput({
  value = '',
  onChange,
  onComplete,
  length = 6,
  autoFocus = true,
  disabled = false,
  error = false,
  testIdPrefix = 'otp-input',
}) {
  const refs = useRef([]);

  // Normalise: only digits, sliced to `length`.
  const digits = useMemo(() => {
    const clean = String(value || '').replace(/\D/g, '').slice(0, length);
    return Array.from({ length }, (_, i) => clean[i] || '');
  }, [value, length]);

  useEffect(() => {
    if (autoFocus && refs.current[0] && !disabled) {
      refs.current[0].focus();
    }
  }, [autoFocus, disabled]);

  const emit = useCallback(
    (nextDigits) => {
      const next = nextDigits.join('');
      if (typeof onChange === 'function') onChange(next);
      if (next.length === length && typeof onComplete === 'function') {
        onComplete(next);
      }
    },
    [onChange, onComplete, length]
  );

  const handleChange = (i, e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      // Pure deletion in a box.
      const next = [...digits];
      next[i] = '';
      emit(next);
      return;
    }
    // If the user pasted or typed more than one digit into a single box,
    // spread the incoming digits across the boxes from `i` onwards.
    const next = [...digits];
    for (let k = 0; k < raw.length && i + k < length; k += 1) {
      next[i + k] = raw[k];
    }
    emit(next);
    const nextFocus = Math.min(i + raw.length, length - 1);
    refs.current[nextFocus]?.focus();
    refs.current[nextFocus]?.select?.();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      // Move focus back and clear the previous digit.
      const next = [...digits];
      next[i - 1] = '';
      emit(next);
      refs.current[i - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      refs.current[i + 1]?.focus();
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    const next = Array.from({ length }, (_, k) => pasted[k] || '');
    emit(next);
    const nextFocus = Math.min(pasted.length, length - 1);
    refs.current[nextFocus]?.focus();
    refs.current[nextFocus]?.select?.();
  };

  return (
    <div
      className="flex items-center justify-between gap-2 sm:gap-3"
      onPaste={handlePaste}
      data-testid={`${testIdPrefix}-root`}
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select?.()}
          data-testid={`${testIdPrefix}-${i}`}
          aria-label={`Digit ${i + 1} of ${length}`}
          className={`w-11 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-semibold rounded-lg border-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors ${
            error
              ? 'border-rose-400 text-rose-700'
              : d
              ? 'border-orange-400 text-slate-900'
              : 'border-slate-200 text-slate-900 hover:border-slate-300'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        />
      ))}
    </div>
  );
}
